import { useEffect, useMemo, useState } from "react";
import { Search, RotateCcw } from "lucide-react";
import { api, waDefault } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Eyebrow, FadeIn } from "@/components/Reveal";

const OPERATIONS = [
  { key: "todos", label: "Todos" },
  { key: "venta", label: "Venta" },
  { key: "alquiler", label: "Alquiler" },
];

const TYPES = ["Piso", "Casa", "Chalet", "Villa", "Ático", "Apartamento", "Finca", "Terreno", "Local", "Otro"];

const Propiedades = () => {
  const [items, setItems] = useState(null);
  const [operacion, setOperacion] = useState("todos");
  const [tipo, setTipo] = useState("todos");
  const [q, setQ] = useState("");

  useEffect(() => {
    api
      .get("/properties")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter(
      (p) =>
        (operacion === "todos" || p.operation === operacion) &&
        (tipo === "todos" || p.property_type === tipo) &&
        (!q || `${p.title} ${p.location} ${p.description} ${p.ref}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [items, operacion, tipo, q]);

  const reset = () => {
    setOperacion("todos");
    setTipo("todos");
    setQ("");
  };

  return (
    <main className="mx-auto max-w-7xl px-6 pb-28 pt-32 md:px-8 md:pt-40">
      <Eyebrow>Catálogo</Eyebrow>
      <h1 className="mt-4 font-serif text-4xl uppercase tracking-wide text-ivory md:text-6xl">
        Nuestras <span className="text-gold-metallic">propiedades</span>
      </h1>

      <div className="mt-10 flex flex-col gap-4 rounded-2xl border border-gold/20 bg-ink2/70 p-5 md:flex-row md:items-center md:justify-between" data-testid="properties-filter-bar">
        <div className="flex gap-2">
          {OPERATIONS.map((o) => (
            <button
              key={o.key}
              onClick={() => setOperacion(o.key)}
              data-testid={`filter-tab-${o.key}`}
              className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-all ${
                operacion === o.key ? "btn-gold-metallic text-ink" : "border border-gold/25 text-gold hover:bg-gold/10"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            data-testid="property-type-select"
            className="rounded-lg border border-gold/25 bg-ink px-4 py-2.5 text-sm text-ivory outline-none focus:border-gold"
          >
            <option value="todos">Todos los tipos</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gold-subtle" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por zona, título…"
              data-testid="properties-search-input"
              className="w-full rounded-lg border border-gold/25 bg-ink py-2.5 pl-10 pr-4 text-sm text-ivory placeholder:text-stone2/60 outline-none focus:border-gold sm:w-56"
            />
          </div>
        </div>
      </div>

      <p className="mt-8 text-sm text-stone2" data-testid="properties-count">
        {items === null ? "Cargando…" : `${filtered.length} ${filtered.length === 1 ? "propiedad" : "propiedades"}`}
      </p>

      <div className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p, i) => (
          <PropertyCard key={p.id} p={p} index={i} />
        ))}
      </div>

      {items !== null && filtered.length === 0 && (
        <div className="mt-10 rounded-2xl border border-gold/15 bg-ink2/70 p-12 text-center" data-testid="properties-empty">
          <p className="font-serif text-2xl text-ivory">No hay propiedades que coincidan con tu búsqueda.</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={reset}
              data-testid="properties-reset-button"
              className="flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-gold transition-colors hover:bg-gold/10"
            >
              <RotateCcw size={15} /> Restablecer filtros
            </button>
            <a
              href={waDefault()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="properties-empty-whatsapp"
              className="rounded-full bg-wa px-6 py-3 text-sm font-semibold text-ink"
            >
              Pregúntanos por WhatsApp
            </a>
          </div>
        </div>
      )}

      <FadeIn className="mt-20">
        <div className="rounded-2xl border border-gold/20 bg-ink2/60 p-10 text-center">
          <h2 className="font-serif text-2xl uppercase text-ivory md:text-4xl">
            ¿No encuentras lo que <span className="text-gold-metallic">buscas</span>?
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm text-stone2">
            Tenemos acceso a muchas más propiedades. Dinos qué necesitas y te enviamos opciones por WhatsApp hoy mismo.
          </p>
          <a
            href={waDefault()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="properties-cta-whatsapp"
            className="btn-gold-metallic mt-8 inline-block rounded-full px-8 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-ink"
          >
            Escríbenos {""}
          </a>
        </div>
      </FadeIn>
    </main>
  );
};

export default Propiedades;
